import React from 'react';
import { useFormContext } from 'react-hook-form';
import useDeepMemo from 'hooks/useDeepMemo';

const sanitizeId = (value) => String(value || 'field').replace(/[^a-zA-Z0-9_-]/g, '-');

export const mergeDescribedBy = (...values) => values.filter(Boolean).join(' ') || undefined;

const useFormControl = ({ field, validation, id, ariaDescribedBy }) => {
  const methods = useFormContext();
  const { clearErrors, register, setValue } = methods;
  const stableValidation = useDeepMemo(() => validation, [validation]);
  const generatedId = React.useId().replace(/:/g, '');
  const inputId = id || `bw-field-${sanitizeId(field)}-${generatedId}`;
  const errorId = `${inputId}-error`;
  const { error } = methods.getFieldState(field, methods.formState);
  const hasError = Boolean(error);
  const errorMessage = error?.message;
  const [isFlashing, setIsFlashing] = React.useState(false);
  const submitCount = methods.formState.submitCount;

  React.useEffect(() => {
    if (!field) return undefined;
    register(field, stableValidation);
    return undefined;
  }, [field, register, stableValidation]);

  React.useEffect(() => {
    if (!hasError) {
      setIsFlashing(false);
      return undefined;
    }

    setIsFlashing(false);
    const startTimer = window.setTimeout(() => setIsFlashing(true), 0);
    const stopTimer = window.setTimeout(() => setIsFlashing(false), 1800);

    return () => {
      window.clearTimeout(startTimer);
      window.clearTimeout(stopTimer);
    };
  }, [errorMessage, hasError, submitCount]);

  const setFieldValue = React.useCallback(
    (value, options = {}) => {
      clearErrors(field);
      setValue(field, value, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
        ...options,
      });
    },
    [clearErrors, field, setValue],
  );

  return {
    methods,
    error,
    errorId,
    errorMessage,
    fieldValue: methods.watch(field),
    hasError,
    inputId,
    isFlashing,
    describedBy: mergeDescribedBy(ariaDescribedBy, hasError ? errorId : undefined),
    setFieldValue,
  };
};

export default useFormControl;
